import { useContext, useState } from 'react';
import { Button, NativeSelect, Stack } from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useEquifaxIdMatrixForm,
  useRunEquifaxIdMatrixRequest,
} from '~/core/shared/api/plugin';
import { Dialog } from '~/core/shared/components';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import {
  ApplicationContext,
  DocumentDetailsViewActionContext,
} from '../../Layout.component';

/**
 * @TODO Prevent this modal from being eager loaded on every application load.
 */
export const IdMatrixModal = () => {
  const { applicationUuid, setLastApplicationUpdate, actionContexts } =
    useContext(ApplicationContext);

  const {
    action: { key },
  } = useContext(DocumentDetailsViewActionContext);

  const equifaxModalApi: UseOpenCloseReturn = actionContexts[key].modal;

  const { formData, isLoading: isFormLoading } =
    useEquifaxIdMatrixForm(applicationUuid);

  const { runEquifaxIdMatrixRequest, isPending } =
    useRunEquifaxIdMatrixRequest();

  const [selectedUuid, setSelectedUuid] = useState<string>();

  const isLoading = isFormLoading || isPending;

  const startRequest = useEventHandler(
    async (individualUuid: string): Promise<void> => {
      try {
        await runEquifaxIdMatrixRequest({
          params: {
            uuid: applicationUuid,
            individualUuid,
          },
        });

        setLastApplicationUpdate(new Date());
        equifaxModalApi.onClose();
      } catch {
        // Error is handled in useRunEquifaxIdMatrixRequest
      }
    },
  );

  return (
    <Dialog
      header="Equifax - ID Matrix"
      onOpenChange={equifaxModalApi.onOpenChange}
      open={equifaxModalApi.open}
      size="lg"
    >
      <Stack gap={4} p={6}>
        {formData && (
          <NativeSelect.Root>
            <NativeSelect.Field
              onChange={({ currentTarget }) =>
                setSelectedUuid(currentTarget.value)
              }
              placeholder="Select individual"
            >
              {Object.entries(formData.individuals).map(([, data], index) => (
                // eslint-disable-next-line react/no-array-index-key
                <optgroup key={index} label={data.optGroup}>
                  {data.options.map((option, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <option key={idx} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        )}

        <Stack direction="row" justifyContent="end">
          <Button
            colorPalette="gray"
            fontSize="md"
            fontWeight="normal"
            onClick={equifaxModalApi.onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            colorPalette="primary"
            disabled={!selectedUuid}
            fontSize="md"
            fontWeight="bold"
            loading={isLoading}
            onClick={() => selectedUuid && startRequest(selectedUuid)}
            variant="solid"
          >
            Check ID
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
